
import { DecimalPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, OnChanges, NgZone } from '@angular/core';

import { filter, Subscription, throwError, combineLatest, Observable, BehaviorSubject } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SignificantDigitsPipe } from '../../../Misc/pipes/SignificantDigits/significant-digits.pipe';
import { numberWithSeparators } from '../../../Misc/helpers/formatting/number.helper';
import { DataPointModel } from '../../../../Common/Charts/models/data-point.model';
import { DataListChartComponent } from '../DataListChart/data-list-chart.component';
import { chartColors } from '../../enums/chart-colors.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { WordCloudChartOptionsModel } from '../../models/WordCloudChart/word-cloud-chart-options.model';
import { ChartMetricsEnum } from '../../../Reports/enums/chart-metrics.enum';
import { ReportSeriesModel } from '../../../Reports/models/report-series.model';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { TermTypesEnum } from '../../../../Common/Reports/enums/term-types.enum';
import { SeriesModel } from '../../../../Common/Charts/models/series.model';
export interface TopTermEvent {
    url: string;
    label: string;
}

@Component({
    selector: 'agpr-top-terms-chart',
    templateUrl: './top-terms-chart.component.html',
    styleUrls: ['./top-terms-chart.component.scss'],
    standalone: false
})
export class TopTermsChartComponent extends DataListChartComponent implements OnInit, OnDestroy, OnChanges {

    @Input() accentColor: string = userAppPreferencesDefault.reportsPrefs.defaultChartColors.otherColors.impressions;
    @Input() header: string = '';
    @Input() initialLabel: string;
    @Input() tooltipSelectsTerm: boolean = false;
    @Input() numOfItems: number = 5;
    @Input() focusable: boolean = true;
    @Input() hideViewPostsLink: boolean = false;
    @Input() chartOptions: WordCloudChartOptionsModel = {};
    @Input() noContentMessage: string = 'No Content to Display';

    @Output() termSelected = new EventEmitter<TopTermEvent>();
    @Output() tooltipLinkClicked = new EventEmitter<TopTermEvent>();
    @Output() dataChange = new EventEmitter<boolean>();
    @Output() sourceDeleted: EventEmitter<boolean> = new EventEmitter();

    currentTerm: DataPointModel = null;
    hideValue: boolean = false;

    chartMetric: ChartMetricsEnum;
    chartMetricsEnum = ChartMetricsEnum;

    private seriesTrigger$ = new BehaviorSubject<SeriesModel[]>([]);
    private watch$: Observable<any>;


    private subscriptions: Subscription[] = [];

    readonly agprWhite = chartColors.agprWhite;
    readonly messageSeverities = messageSeverityEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;

    constructor(
        public elementRef: ElementRef,
        public decimalPipe: DecimalPipe,
        public currencyPipe: CurrencyPipe,
        public datePipe: DatePipe,
        public significantDigitsPipe: SignificantDigitsPipe,
        public zone: NgZone
    ) {
        super(
            elementRef,
            decimalPipe,
            currencyPipe,
            datePipe,
            significantDigitsPipe,
            zone
        );
    }

    handleLinkClick = (datum: DataPointModel) => {
        if (this.tooltipSelectsTerm) {
            this.currentTerm = this.data.find(item => item.label === datum.label) ?? this.currentTerm;
        }

        this.tooltipLinkClicked.emit({
            url: datum.viewMentionUrl.toString(),
            label: datum.label
        });
        return false;
    };

    ngOnInit(): void {
        super.ngOnInit();

        this.watch$ = this.seriesTrigger$.pipe(
            switchMap((series) => {
                this.loading = true;

                const series$ = series.map(item =>
                    item.dataProvider.pipe(
                        filter(v => !!v),
                        map(data => {
                            this.zone.runOutsideAngular(() => {
                                this.data = data.slice(0, this.numOfItems);
                                this.loading = false;
                            });
                            return data;
                        }),
                        catchError(error => {
                            this.error = error;
                            this.loading = false;
                            if (error.message === 'Data source no longer available') {
                                this.sourceDeleted.emit(true);
                            }
                            return throwError(() => new Error(error));
                        })
                    )
                );

                return combineLatest(series$);
            })
        );
        const sub = this.watch$.subscribe(() => {
            requestAnimationFrame(() => {
                this.dataChange.emit(true);
            });
        });
        this.subscriptions.push(sub);
        if (this.series?.[0]) {
            this.subscriptions.push(
                this.series[0].dataProvider.pipe(
                    filter(x => !!x)
                ).subscribe(data => {
                    this.currentTerm = data.find(datum => datum.label === this.initialLabel) ?? data[0];
                    this.hideValue = (this.series[0] as ReportSeriesModel).termType === TermTypesEnum.WORDS;
                    data.forEach(datum => {
                        let count: string;
                        if (typeof datum.count === 'string') {
                            count = numberWithSeparators(parseInt(datum.count, 10), ',');
                        } else {
                            count = numberWithSeparators(datum.count, ',');
                        }
                        const currencyCode = this.chartMetric === this.chartMetricsEnum.ave && this.chartOptions.currencyCode
                            ? (' ' + this.chartOptions.currencyCode)
                            : '';
                        const dataText =
                        this.hideValue ? '' : this.chartOptions.tooltipDescription1 + count + currencyCode + this.chartOptions.tooltipDescription2;
                        datum.tooltipDataText = dataText;

                        let linkText = this.chartOptions?.viewMentionString ?? '';

                        if (this.hideViewPostsLink) {
                            linkText = '';
                        }

                        datum.tooltipLinkText = linkText;
                    });
                })
            );
        }
    }

    ngOnChanges(): void {
        if (this.series?.[0]) {
            this.hideValue = (this.series[0] as ReportSeriesModel).termType === TermTypesEnum.WORDS;
            this.chartMetric = (this.series[0] as ReportSeriesModel).metric;
        }

        if (this.series && this.series !== this.oldSeries) {
            this.seriesTrigger$.next(this.series);
        }
    }

    selectTerm(selectedTerm: DataPointModel, event: Event) {
        event.stopPropagation();
        this.currentTerm = selectedTerm;
        this.termSelected.emit({
            url: selectedTerm['viewMentionUrl'] as string,
            label: selectedTerm.label
        });
    }

    ngOnDestroy() {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }
}
